// UserScopedStorage.js
// Enterprise-grade user-scoped localStorage system
// Prevents data contamination across different user sessions

export class UserScopedStorage {
    static currentUserId = null;
    static storagePrefix = 'deckops_';
    
    /**
     * Initialize user-scoped storage for current user
     */
    static initialize(userId) {
        if (this.currentUserId !== userId) {
            console.log(`🔧 UserScopedStorage: Switching user context ${this.currentUserId} → ${userId}`);
            this.currentUserId = userId;
            
            // Clean up any global localStorage that should be user-specific
            this.migrateGlobalToUserScoped();
        }
    }
    
    /**
     * Get user-scoped key
     */
    static getUserScopedKey(key) {
        const userId = this.currentUserId || 'guest';
        return `${this.storagePrefix}${userId}_${key}`;
    }
    
    /**
     * Set user-scoped item
     */
    static setItem(key, value) {
        const scopedKey = this.getUserScopedKey(key);
        localStorage.setItem(scopedKey, value);
        console.log(`📦 UserScopedStorage: Set ${key} for user ${this.currentUserId}`);
    }
    
    /**
     * Get user-scoped item
     */
    static getItem(key) {
        const scopedKey = this.getUserScopedKey(key);
        return localStorage.getItem(scopedKey);
    }
    
    /**
     * Remove user-scoped item
     */
    static removeItem(key) {
        const scopedKey = this.getUserScopedKey(key);
        localStorage.removeItem(scopedKey);
    }
    
    /**
     * Clear all data for current user
     */
    static clearUserData() {
        if (!this.currentUserId) return;
        
        const userPrefix = `${this.storagePrefix}${this.currentUserId}_`;
        const keysToRemove = [];
        
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith(userPrefix)) {
                keysToRemove.push(key);
            }
        }
        
        keysToRemove.forEach(key => localStorage.removeItem(key));
        console.log(`🧹 UserScopedStorage: Cleared ${keysToRemove.length} items for user ${this.currentUserId}`);
    }
    
    /**
     * Migrate existing global localStorage to user-scoped
     */
    static migrateGlobalToUserScoped() {
        const criticalKeys = [
            'hasCompletedCalibration',
            'pptHeroScoreData',
            'userAnalyticsData',
            'gameProgress',
            'userPreferences',
            'mobileSettings'
        ];
        
        criticalKeys.forEach(key => {
            const globalValue = localStorage.getItem(key);
            if (globalValue && this.currentUserId) {
                // Move to user-scoped storage
                this.setItem(key, globalValue);
                // Remove global version
                localStorage.removeItem(key);
                console.log(`🔄 UserScopedStorage: Migrated ${key} to user-scoped storage`);
            }
        });
    }
    
    /**
     * Clean up on user logout
     */
    static onUserLogout() {
        console.log(`👋 UserScopedStorage: User ${this.currentUserId} logged out`);
        this.currentUserId = null;
    }
    
    /**
     * Get all user data for debugging
     */
    static getUserData() {
        if (!this.currentUserId) return {};
        
        const userPrefix = `${this.storagePrefix}${this.currentUserId}_`;
        const userData = {};
        
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith(userPrefix)) {
                const originalKey = key.replace(userPrefix, '');
                userData[originalKey] = localStorage.getItem(key);
            }
        }
        
        return userData;
    }
    
    /**
     * Debug utility
     */
    static debugStorage() {
        console.log('🔍 UserScopedStorage Debug:');
        console.log('Current user:', this.currentUserId);
        console.log('User data:', this.getUserData());
        
        // Show global localStorage that might cause conflicts
        const globalKeys = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && !key.startsWith(this.storagePrefix)) {
                globalKeys.push(key);
            }
        }
        
        if (globalKeys.length > 0) {
            console.warn('⚠️ Global localStorage keys that might cause conflicts:', globalKeys);
        }
        
        return {
            currentUser: this.currentUserId,
            userData: this.getUserData(),
            globalKeys: globalKeys
        };
    }
}

// Make available globally for debugging
window.debugUserStorage = () => UserScopedStorage.debugStorage();
